'use client';

/**
 * ClubesView — orchestrator client da rota /clubes facelift.
 *
 * Responsável por:
 *   - State dos filtros (search/neighborhood/partnerOnly)
 *   - Paginação client-side via Server Action `fetchMoreClubsAction`
 *   - View toggle Lista ↔ Mapa (URL ?view=mapa via Next router)
 *   - Reset clubs[] quando filtros mudam (debounce 250ms na busca)
 *
 * Recebe `initialClubs` (primeiros 20) renderizados pelo Server Component
 * pra LCP rápido. Subsequentes paginações vêm da Server Action.
 */
import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { AnimatePresence, motion, useInView } from 'framer-motion';

import { Button } from '@brisa/ui/components/ui/button';
import { cn } from '@brisa/ui/lib/utils';
import type { ClubListItem } from '@brisa/clubs';
import { fetchMoreClubsAction } from '../_actions/fetch-clubs';
import {
  ClubesFilterBar,
  NEIGHBORHOOD_ALL,
  type ClubesFilters,
  type ClubesFilterBarCopy,
  type ClubesView as ClubesViewMode,
} from './ClubesFilterBar';
import { ClubesGrid, type ClubesGridCopy } from './ClubesGrid';
import { ClubesMapSplit, type ClubesMapSplitCopy } from './ClubesMapSplit';
import { Reveal } from './Reveal';

export type { ClubesViewMode };

export interface ClubesViewCopy {
  filterBar: ClubesFilterBarCopy;
  grid: ClubesGridCopy;
  mapSplit: ClubesMapSplitCopy;
}

export interface ClubesViewProps {
  initialClubs: ClubListItem[];
  initialHasMore: boolean;
  initialMode: ClubesViewMode;
  neighborhoods: string[];
  mapboxToken: string | undefined;
  locale: 'pt' | 'es';
  copy: ClubesViewCopy;
}

const PAGE_SIZE = 20;

const EMPTY_FILTERS: ClubesFilters = {
  search: '',
  neighborhood: NEIGHBORHOOD_ALL,
  partnerOnly: false,
};

function hasActiveFilters(f: ClubesFilters): boolean {
  return f.search.trim() !== '' || f.neighborhood !== NEIGHBORHOOD_ALL || f.partnerOnly;
}

function filtersToActionInput(f: ClubesFilters, offset: number) {
  return {
    search: f.search.trim() || undefined,
    neighborhood: f.neighborhood === NEIGHBORHOOD_ALL ? undefined : f.neighborhood,
    partnerOnly: f.partnerOnly || undefined,
    offset,
    limit: PAGE_SIZE,
  };
}

export function ClubesView({
  initialClubs,
  initialHasMore,
  initialMode,
  neighborhoods,
  mapboxToken,
  locale,
  copy,
}: ClubesViewProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mode, setMode] = React.useState<ClubesViewMode>(initialMode);
  const [filters, setFilters] = React.useState<ClubesFilters>(EMPTY_FILTERS);
  const [clubs, setClubs] = React.useState<ClubListItem[]>(initialClubs);
  const [hasMore, setHasMore] = React.useState<boolean>(initialHasMore);
  const [isPending, startTransition] = React.useTransition();

  // --- Sticky search visibility no mobile (Bruno 2026-05-21) ---
  // Regra: enquanto o mapa estiver visível (qualquer pixel dentro do viewport
  // completo, INCLUINDO atrás do header), busca aparece. Quando o mapa sai
  // 100% da tela E o usuário está rolando pra baixo, busca some. Volta ao
  // rolar pra cima (como padrão iOS Safari/header). Desktop: sempre visível.
  const mapColumnRef = React.useRef<HTMLDivElement>(null);
  // Sem margin: usa viewport completo (0→100vh). Mapa "sai da tela" só quando
  // bottom < 0. Atrás do header (0→72px) ainda conta como "na tela".
  const isMapInView = useInView(mapColumnRef);
  const [scrollDir, setScrollDir] = React.useState<'up' | 'down'>('up');
  React.useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        // threshold 4px filtra jitter de trackpad/touch
        if (Math.abs(dy) > 4) {
          setScrollDir(dy > 0 ? 'down' : 'up');
          lastY = y;
        }
        ticking = false;
      });
      ticking = true;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const searchHidden = !isMapInView && scrollDir === 'down';

  // Debounce da busca pra não disparar Server Action a cada keystroke
  const filtersDebouncedRef = React.useRef<ClubesFilters>(EMPTY_FILTERS);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger refetch quando filtros mudam (debounced)
  React.useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const f = filters;
      filtersDebouncedRef.current = f;
      // Se filtros voltaram ao EMPTY, usar initialClubs sem chamar action
      if (!hasActiveFilters(f)) {
        setClubs(initialClubs);
        setHasMore(initialHasMore);
        return;
      }
      startTransition(async () => {
        const result = await fetchMoreClubsAction(filtersToActionInput(f, 0));
        setClubs(result.clubs);
        setHasMore(result.hasMore);
      });
    }, 250);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [filters, initialClubs, initialHasMore]);

  const handleLoadMore = React.useCallback(() => {
    startTransition(async () => {
      const result = await fetchMoreClubsAction(
        filtersToActionInput(filtersDebouncedRef.current, clubs.length),
      );
      setClubs((prev) => [...prev, ...result.clubs]);
      setHasMore(result.hasMore);
    });
  }, [clubs.length]);

  const handleClearFilters = React.useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const handleModeChange = React.useCallback(
    (next: ClubesViewMode) => {
      setMode(next);
      const sp = new URLSearchParams(searchParams.toString());
      if (next === 'map') sp.set('view', 'mapa');
      else sp.delete('view');
      const qs = sp.toString();
      router.replace(`/clubes${qs ? '?' + qs : ''}`, { scroll: false });
    },
    [router, searchParams],
  );

  const emptyVisible = !isPending && clubs.length === 0 && hasActiveFilters(filters);

  const emptyState = (
    <div
      data-slot="clubes-empty"
      className="flex flex-col items-center gap-4 rounded-[24px] bg-[hsl(var(--text-08))] px-8 py-16 text-center"
    >
      <h3 className="font-body text-xl font-medium text-[hsl(var(--text-100))]">
        {copy.grid.emptyTitle}
      </h3>
      <p className="max-w-[42ch] font-body text-sm text-[hsl(var(--text-56))]">
        {copy.grid.emptyBody}
      </p>
      <Button variant="secondary" size="md" onClick={handleClearFilters}>
        {copy.grid.emptyClear}
      </Button>
    </div>
  );

  return (
    <div data-slot="clubes-view" className="flex flex-col gap-3 md:gap-24">
      {/* FilterBar — sticky no mobile (top: 72px = altura do header fixed).
          Bleed para fora do section padding via -mx-6 / -mx-10 para encostar
          nas bordas do viewport. Sem background nem shadow no wrapper — a
          elevação é aplicada no próprio Input.
          Show/hide (Bruno 2026-05-21): enquanto o mapa está visível OU o
          usuário está rolando pra cima → searchHidden=false (visible).
          Mapa fora da viewport + rolando pra baixo → translate-y pra cima
          (some atrás do header). Desktop: md:translate-y-0 neutraliza. */}
      <div
        data-slot="clubes-filterbar-sticky"
        className={cn(
          'sticky top-[72px] z-30 -mx-6 px-6 py-3 transition-transform duration-200 ease-out sm:-mx-10 sm:px-10 md:static md:top-auto md:z-auto md:mx-0 md:translate-y-0 md:p-0 md:transition-none',
          searchHidden ? '-translate-y-[calc(100%+72px)]' : 'translate-y-0',
        )}
      >
        <Reveal as="div" delay={0.1} rootMargin="0px">
          <ClubesFilterBar
            copy={copy.filterBar}
            neighborhoods={neighborhoods}
            filters={filters}
            onFiltersChange={setFilters}
            view={mode}
            onViewChange={handleModeChange}
          />
        </Reveal>
      </div>

      {/* ============ Mobile (< md) ============
          Mapa + lista coexistem SEMPRE (mobile-tweaks-v2 lote 2 — Bruno 2026-05-21).
          ClubesMapSplit já tem branch mobile com mapa no topo + lista abaixo. */}
      <div className="md:hidden">
        {emptyVisible ? (
          emptyState
        ) : (
          <ClubesMapSplit
            clubs={clubs}
            hasMore={hasMore}
            isPending={isPending}
            onLoadMore={handleLoadMore}
            mapboxToken={mapboxToken}
            locale={locale}
            copy={copy.mapSplit}
            mapColumnRef={mapColumnRef}
          />
        )}
      </div>

      {/* ============ Desktop (md+) ============
          Cross-fade entre Lista, Mapa e Empty conforme toggle do FilterBar. */}
      <div className="hidden md:block">
        <AnimatePresence mode="wait">
          {emptyVisible ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {emptyState}
            </motion.div>
          ) : mode === 'list' ? (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <ClubesGrid
                clubs={clubs}
                hasMore={hasMore}
                isPending={isPending}
                onLoadMore={handleLoadMore}
                copy={copy.grid}
              />
            </motion.div>
          ) : (
            <motion.div
              key="map"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <ClubesMapSplit
                clubs={clubs}
                hasMore={hasMore}
                isPending={isPending}
                onLoadMore={handleLoadMore}
                mapboxToken={mapboxToken}
                locale={locale}
                copy={copy.mapSplit}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
